import { Link } from 'react-router-dom';
import { StatusChip } from '../../components/index.js';

/**
 * Presentational modules for the RAPID v2 Readiness QA redesign.
 * Mirrors the Metrics/Diagnosis view-switcher pattern: an always-on, count-driven QA summary + a
 * section nav whose Expand-all/Collapse button is a sibling OUTSIDE the role=tablist (axe). The
 * default visible view is "Readiness Checks" (the findings + convert buttons), so the conversion
 * workflow stays interactive on load. The owning QaTab keeps convertFinding + state.
 *
 * Findings, convert buttons (`Convert to {Type}`), and the lineage string
 * (`Lineage: {findingId} → {targetType} {recordId}` + `(subtype)`) are preserved verbatim.
 */

export const QA_SECTIONS = [
  { key: 'checks', label: 'Readiness Checks' },
  { key: 'conversions', label: 'Conversion Center' },
  { key: 'impact', label: 'Leadership Impact' },
  { key: 'traceability', label: 'Traceability' },
];

export const QA_DEFAULT_SECTION = 'checks';

const SEVERITY_RANK = {
  critical: 5,
  high: 4,
  'medium-high': 4,
  medium: 3,
  'low-medium': 2,
  low: 1,
};

// targetType → where the generated record surfaces (label + in-page link).
const CONVERSION_TARGET = {
  Action: { tab: 'Actions', to: '?tab=actions' },
  RFI: { tab: 'Intake', to: '?tab=intake' },
  Decision: { tab: 'Governance', to: '?tab=governance&gv=decisions' },
  Assumption: { tab: 'Scope Canvas', to: '?tab=canvas' },
  Barrier: { tab: 'Governance', to: '?tab=governance&gv=decisions' },
  'Data Issue': { tab: 'Metrics & Value', to: '?tab=metrics' },
};

// category → what an unresolved finding blocks for leadership.
const CATEGORY_IMPACT = {
  'Process fidelity': 'final criteria confidence and the timeline model',
  'Scoring governance': 'criteria-threshold defensibility',
  'Data quality': 'the production value claim and metric source-of-truth',
  'Pod operating model': 'delivery capacity and closeout readiness',
};

function impactFor(category) {
  return CATEGORY_IMPACT[category] || 'closeout readiness and the governance brief';
}

export function deriveQaConversionState(findings = [], detailState = {}) {
  const conversions = detailState.conversions || [];
  const byFinding = new Map();
  for (const finding of findings) {
    byFinding.set(
      finding.id,
      conversions.filter((entry) => entry.findingId === finding.id),
    );
  }
  const countsByType = {};
  for (const entry of conversions) {
    if (!findings.some((f) => f.id === entry.findingId)) continue;
    countsByType[entry.targetType] = (countsByType[entry.targetType] || 0) + 1;
  }
  const convertedCount = findings.filter((f) => (byFinding.get(f.id) || []).length > 0).length;
  const allConverted = findings.length > 0 && convertedCount === findings.length;
  const highestSeverity =
    findings
      .map((f) => f.severity)
      .sort(
        (a, b) =>
          (SEVERITY_RANK[String(b).toLowerCase()] || 0) -
          (SEVERITY_RANK[String(a).toLowerCase()] || 0),
      )[0] || '—';
  return { byFinding, countsByType, convertedCount, allConverted, highestSeverity, conversions };
}

// ---------------------------------------------------------------------------
// Section nav
// ---------------------------------------------------------------------------

export function QaSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="qa-section-nav" data-testid="qa-section-nav">
      <div className="qa-nav-tabs" role="tablist" aria-label="Readiness QA sections">
        {QA_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`qa-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`qa-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`qa-nav-chip qa-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="qa-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? QA_DEFAULT_SECTION : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Always-on, count-driven QA summary
// ---------------------------------------------------------------------------

export function QaSummaryPanel({ findings, conversionState }) {
  const { convertedCount, allConverted, highestSeverity, countsByType } = conversionState;
  const total = findings.length;
  const open = total - convertedCount;
  const typeSummary = Object.entries(countsByType)
    .map(([type, count]) => `${count} ${type}`)
    .join(' · ');
  return (
    <section className="rapid-panel qa-summary-panel" data-testid="qa-summary-panel">
      <div className="qa-summary-head">
        <div className="row spread">
          <div>
            <div className="eyebrow">Readiness QA</div>
            <h3 className="qa-summary-headline" data-testid="qa-summary-headline">
              {total} readiness {total === 1 ? 'check' : 'checks'} requiring owner action
            </h3>
          </div>
          <span className="chip outline sm" data-testid="qa-summary-state">
            {allConverted ? 'Actions created' : 'Owner action needed'}
          </span>
        </div>
        <p className="muted small">
          Deterministic checks that must be resolved or accepted before leadership readout.
        </p>
      </div>

      <div className="qa-summary-stats">
        <div className="qa-summary-stat is-primary">
          <span className="k">Open checks</span>
          <span className="v" data-testid="qa-summary-open">
            {open}
          </span>
          <span className="sub">{convertedCount} converted to tracked work</span>
        </div>
        <div className="qa-summary-stat">
          <span className="k">Total checks</span>
          <span className="v">{total}</span>
          <span className="sub">Authoritative, seed-bound</span>
        </div>
        <div className="qa-summary-stat">
          <span className="k">Highest severity</span>
          <span className="v is-text">{highestSeverity}</span>
          <span className="sub">Across open findings</span>
        </div>
        <div className="qa-summary-stat">
          <span className="k">Converted records</span>
          <span className="v is-text" data-testid="qa-summary-converted-types">
            {typeSummary || 'None yet'}
          </span>
          <span className="sub">By destination type</span>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Readiness Checks (findings grouped by category) — default view
// ---------------------------------------------------------------------------

function FindingCard({ finding, conversions, onConvert }) {
  return (
    <article className="rapid-finding-card" data-testid={`qa-finding-${finding.id}`}>
      <div className="row spread">
        <div>
          <strong>
            {finding.id} · {finding.title}
          </strong>
          <div className="muted small">
            {finding.category} · {finding.severity}
          </div>
        </div>
        <StatusChip status={finding.severity}>{finding.severity}</StatusChip>
      </div>
      <p>{finding.finding}</p>
      <div className="rapid-note">{finding.uiCopy}</div>
      <p>
        <strong>Recommended resolution:</strong> {finding.proposedResolution}
      </p>
      <div className="rapid-inline-actions">
        {(finding.convertTo || []).map((targetType) => (
          <button
            key={targetType}
            type="button"
            className="btn ghost sm"
            onClick={() => onConvert(finding, targetType)}
          >
            Convert to {targetType}
          </button>
        ))}
      </div>
      {conversions.map((conversion) => (
        <div className="rapid-lineage-row" key={conversion.recordId}>
          <strong>Lineage:</strong> {conversion.findingId} → {conversion.targetType}{' '}
          {conversion.recordId}
          {conversion.barrierSubtype ? ` (${conversion.barrierSubtype})` : ''}
        </div>
      ))}
    </article>
  );
}

export function ReadinessChecksView({ findings, conversionState, onConvert }) {
  const groups = [];
  const indexByCategory = new Map();
  for (const finding of findings) {
    const category = finding.category || 'Other';
    if (!indexByCategory.has(category)) {
      indexByCategory.set(category, groups.length);
      groups.push({ category, items: [] });
    }
    groups[indexByCategory.get(category)].items.push(finding);
  }
  return (
    <section className="rapid-panel" data-testid="qa-checks-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Readiness checks</div>
          <h3>Resolve or accept each check before readout</h3>
        </div>
      </div>
      {groups.map((group) => (
        <div className="qa-check-group" key={group.category}>
          <h4 className="qa-check-group-title">{group.category}</h4>
          <div className="rapid-record-list">
            {group.items.map((finding) => (
              <FindingCard
                key={finding.id}
                finding={finding}
                conversions={conversionState.byFinding.get(finding.id) || []}
                onConvert={onConvert}
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Conversion Center (converted records grouped by destination)
// ---------------------------------------------------------------------------

export function ConversionCenterView({ conversionState }) {
  const { conversions } = conversionState;
  const byType = new Map();
  for (const entry of conversions) {
    if (!byType.has(entry.targetType)) byType.set(entry.targetType, []);
    byType.get(entry.targetType).push(entry);
  }
  return (
    <section className="rapid-panel" data-testid="qa-conversions-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Conversion center</div>
          <h3>Tracked work created from QA findings</h3>
        </div>
      </div>
      {conversions.length === 0 ? (
        <div className="rapid-empty-inline">
          No conversions yet. Convert a readiness check to create tracked work.
        </div>
      ) : (
        [...byType.entries()].map(([targetType, entries]) => {
          const dest = CONVERSION_TARGET[targetType];
          return (
            <div className="qa-conversion-group" key={targetType}>
              <div className="row spread">
                <h4>
                  {targetType} ({entries.length})
                </h4>
                {dest ? (
                  <Link className="btn ghost sm" to={dest.to}>
                    Open {dest.tab}
                  </Link>
                ) : null}
              </div>
              <ul className="rapid-record-list">
                {entries.map((entry) => (
                  <li key={entry.recordId}>
                    <strong>{entry.recordId}</strong>
                    <div className="muted small">
                      From {entry.findingId}
                      {entry.barrierSubtype ? ` · ${entry.barrierSubtype}` : ''}
                    </div>
                    <p>{entry.title}</p>
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Leadership Impact
// ---------------------------------------------------------------------------

export function LeadershipImpactView({ findings, conversionState }) {
  return (
    <section className="rapid-panel" data-testid="qa-impact-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Leadership impact</div>
          <h3>What each unresolved check blocks</h3>
        </div>
      </div>
      <ul className="rapid-record-list">
        {findings.map((finding) => {
          const resolved = (conversionState.byFinding.get(finding.id) || []).length > 0;
          return (
            <li key={finding.id}>
              <div className="row spread">
                <strong>
                  {finding.id} · {finding.title}
                </strong>
                <StatusChip status={resolved ? 'done' : finding.severity}>
                  {resolved ? 'Tracked' : 'Open'}
                </StatusChip>
              </div>
              <p>
                {resolved ? 'Resolved — no longer blocks. ' : 'Until resolved, this blocks '}
                {resolved ? '' : impactFor(finding.category)}
                {resolved ? '' : '.'}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Traceability
// ---------------------------------------------------------------------------

export function QaTraceabilityView({ findings, conversionState }) {
  return (
    <section className="rapid-panel" data-testid="qa-traceability-view">
      <div className="rapid-section-heading">
        <div>
          <div className="eyebrow">Traceability</div>
          <h3>Source finding → generated record → impacted surface</h3>
        </div>
      </div>
      <ul className="rapid-record-list">
        {findings.map((finding) => {
          const entries = conversionState.byFinding.get(finding.id) || [];
          return (
            <li key={finding.id}>
              <strong>{finding.id}</strong>
              <div className="muted small">{finding.title}</div>
              {entries.length === 0 ? (
                <p className="muted small">No record generated yet.</p>
              ) : (
                <ul className="rapid-inline-list">
                  {entries.map((entry) => {
                    const dest = CONVERSION_TARGET[entry.targetType];
                    return (
                      <li key={entry.recordId}>
                        {entry.findingId} → {entry.targetType} {entry.recordId}
                        {dest ? (
                          <>
                            {' → '}
                            <Link to={dest.to}>{dest.tab}</Link>
                          </>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
